import React, { useEffect, useState } from "react";
import { api, formatErr, fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { CalendarClock, Target, Building, Repeat, Plus, Trash2, PlayCircle, Printer, FileText, Scale, Save } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { CURRENCY_CODES, companyCurrencies } from "@/lib/currencies";
import InvoiceTemplateDesigner from "./InvoiceTemplateDesigner";

const EXP_CATEGORIES = ["إيجار", "رواتب", "كهرباء وماء", "اتصالات وانترنت", "مواصلات ووقود", "صيانة", "قرطاسية", "تسويق ودعاية", "ضرائب ورسوم", "بنك وعمولات", "أخرى"];

export default function FinanceExtras() {
  const { currentCompany } = useAuth();
  const cur = currentCompany?.base_currency || "SYP";
  const curList = companyCurrencies(currentCompany);
  const [tab, setTab] = useState("aging");

  return (
    <div className="space-y-6" data-testid="finance-extras-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-heading font-bold">التحليلات المالية المتقدمة</h1>
        <p className="text-sm text-muted-foreground mt-1">أعمار الديون · الموازنة مقابل الفعلي · مراكز التكلفة · المصروفات المتكررة</p>
      </div>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="aging" data-testid="fx-tab-aging"><CalendarClock className="w-4 h-4 me-1" /> أعمار الديون</TabsTrigger>
          <TabsTrigger value="budget" data-testid="fx-tab-budget"><Target className="w-4 h-4 me-1" /> الموازنة</TabsTrigger>
          <TabsTrigger value="cost" data-testid="fx-tab-cost"><Building className="w-4 h-4 me-1" /> مراكز التكلفة</TabsTrigger>
          <TabsTrigger value="recurring" data-testid="fx-tab-recurring"><Repeat className="w-4 h-4 me-1" /> مصروفات متكررة</TabsTrigger>
          <TabsTrigger value="invoice" data-testid="fx-tab-invoice"><FileText className="w-4 h-4 me-1" /> إعدادات الفاتورة</TabsTrigger>
          <TabsTrigger value="scale" data-testid="fx-tab-scale"><Scale className="w-4 h-4 me-1" /> الميزان الإلكتروني</TabsTrigger>
        </TabsList>
        <TabsContent value="aging"><Aging cur={cur} /></TabsContent>
        <TabsContent value="budget"><Budget cur={cur} /></TabsContent>
        <TabsContent value="cost"><CostCenters cur={cur} /></TabsContent>
        <TabsContent value="recurring"><Recurring cur={cur} currencies={curList} /></TabsContent>
        <TabsContent value="invoice"><InvoiceSettings /></TabsContent>
        <TabsContent value="scale"><ScaleSettings /></TabsContent>
      </Tabs>
    </div>
  );
}

function Aging({ cur }) {
  const [kind, setKind] = useState("customer");
  const [data, setData] = useState(null);
  const load = async () => { try { const { data } = await api.get(`/reports/aging?kind=${kind}`); setData(data); } catch (e) { toast.error(formatErr(e)); } };
  useEffect(() => { load(); }, [kind]); // eslint-disable-line
  const exportAging = async (fmt) => {
    try {
      const res = await api.get(`/reports/export/aging?kind=${kind}&format=${fmt}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `aging_${kind}.${fmt === "pdf" ? "pdf" : "xlsx"}`; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error("فشل التصدير"); }
  };
  const B = data?.base_currency || cur;
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex gap-1 border rounded-lg p-1 bg-muted/30">
          <Button size="sm" variant={kind === "customer" ? "default" : "ghost"} onClick={() => setKind("customer")} data-testid="aging-customer">ذمم العملاء (لنا)</Button>
          <Button size="sm" variant={kind === "supplier" ? "default" : "ghost"} onClick={() => setKind("supplier")} data-testid="aging-supplier">ذمم الموردين (علينا)</Button>
        </div>
        <Button size="sm" variant="outline" onClick={() => exportAging("excel")} data-testid="aging-export-excel">Excel</Button>
        <Button size="sm" variant="outline" onClick={() => exportAging("pdf")} data-testid="aging-export-pdf"><Printer className="w-4 h-4 me-1" /> PDF</Button>
      </div>
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[["0-30 يوم", "b0", "emerald"], ["31-60 يوم", "b30", "amber"], ["61-90 يوم", "b60", "orange"], ["أكثر من 90", "b90", "red"], ["الإجمالي", "grand", "primary"]].map(([lbl, key, c]) => (
            <Card key={key} className="p-3">
              <div className="text-xs text-muted-foreground">{lbl}</div>
              <div className={`text-lg font-bold tabular-nums mt-1 text-${c}-600`}>{fmtMoney(key === "grand" ? data.grand_total : data.totals[key], B)}</div>
            </Card>
          ))}
        </div>
      )}
      <div className="border border-border rounded-md bg-card overflow-hidden">
        <Table>
          <TableHeader><TableRow>
            <TableHead className="text-start">{kind === "customer" ? "العميل" : "المورد"}</TableHead>
            <TableHead className="text-start">0-30</TableHead><TableHead className="text-start">31-60</TableHead>
            <TableHead className="text-start">61-90</TableHead><TableHead className="text-start">+90</TableHead>
            <TableHead className="text-start">الإجمالي</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {(!data?.rows || data.rows.length === 0) && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">لا توجد ديون قائمة</TableCell></TableRow>}
            {data?.rows?.map((r, i) => (
              <TableRow key={i} data-testid={`aging-row-${i}`}>
                <TableCell className="font-semibold">{r.party_name}</TableCell>
                <TableCell className="tabular-nums">{fmtMoney(r.b0, B)}</TableCell>
                <TableCell className="tabular-nums text-amber-700">{fmtMoney(r.b30, B)}</TableCell>
                <TableCell className="tabular-nums text-orange-700">{fmtMoney(r.b60, B)}</TableCell>
                <TableCell className="tabular-nums text-red-700 font-bold">{fmtMoney(r.b90, B)}</TableCell>
                <TableCell className="tabular-nums font-bold">{fmtMoney(r.total, B)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function Budget({ cur }) {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [cmp, setCmp] = useState(null);
  const [salesTarget, setSalesTarget] = useState("");
  const [expenseCap, setExpenseCap] = useState("");
  const [catCaps, setCatCaps] = useState({});
  const load = async () => {
    try {
      const [b, c] = await Promise.all([api.get(`/budgets?month=${month}`), api.get(`/budgets/compare?month=${month}`)]);
      setSalesTarget(String(b.data.sales_target || ""));
      setExpenseCap(String(b.data.expense_cap || ""));
      setCatCaps(b.data.category_caps || {});
      setCmp(c.data);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, [month]); // eslint-disable-line
  const save = async () => {
    try {
      const cleanCaps = Object.fromEntries(Object.entries(catCaps).filter(([, v]) => +v > 0).map(([k, v]) => [k, +v]));
      await api.put("/budgets", { month, sales_target: +salesTarget || 0, expense_cap: +expenseCap || 0, category_caps: cleanCaps });
      toast.success("تم حفظ الموازنة"); load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const B = cmp?.base_currency || cur;
  const pct = (a, t) => (t > 0 ? Math.min(100, Math.round((a / t) * 100)) : 0);
  const Bar = ({ actual, target, good }) => {
    const p = pct(actual, target);
    const over = target > 0 && actual > target;
    const color = good ? "bg-emerald-500" : over ? "bg-red-500" : "bg-primary";
    return (
      <div className="h-3 rounded-full bg-muted overflow-hidden mt-2">
        <div className={`h-full ${color} transition-all`} style={{ width: `${p}%` }} />
      </div>
    );
  };
  return (
    <div className="space-y-4">
      <div className="flex items-end gap-2 flex-wrap">
        <div><Label className="text-xs">الشهر</Label><Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 w-40" data-testid="budget-month" /></div>
        <div><Label className="text-xs">سقف المبيعات المستهدف ({B})</Label><Input type="number" value={salesTarget} onChange={(e) => setSalesTarget(e.target.value)} className="h-9 w-44" data-testid="budget-sales-target" /></div>
        <div><Label className="text-xs">سقف المصروفات ({B})</Label><Input type="number" value={expenseCap} onChange={(e) => setExpenseCap(e.target.value)} className="h-9 w-44" data-testid="budget-expense-cap" /></div>
        <Button onClick={save} className="h-9" data-testid="budget-save">حفظ</Button>
      </div>
      {cmp && (
        <div className="grid md:grid-cols-2 gap-4">
          <Card className="p-5">
            <div className="flex items-center justify-between"><span className="font-bold">المبيعات</span><span className="text-xs text-muted-foreground">المستهدف {fmtMoney(cmp.sales_target, B)}</span></div>
            <div className="text-2xl font-bold tabular-nums mt-1 text-emerald-700" data-testid="budget-sales-actual">{fmtMoney(cmp.sales_actual, B)}</div>
            <Bar actual={cmp.sales_actual} target={cmp.sales_target} good={cmp.sales_actual >= cmp.sales_target && cmp.sales_target > 0} />
            <div className="text-xs text-muted-foreground mt-1">{pct(cmp.sales_actual, cmp.sales_target)}% من المستهدف</div>
          </Card>
          <Card className="p-5">
            <div className="flex items-center justify-between"><span className="font-bold">المصروفات</span><span className="text-xs text-muted-foreground">السقف {fmtMoney(cmp.expense_cap, B)}</span></div>
            <div className={`text-2xl font-bold tabular-nums mt-1 ${cmp.expense_cap > 0 && cmp.expense_actual > cmp.expense_cap ? "text-red-600" : "text-foreground"}`} data-testid="budget-expense-actual">{fmtMoney(cmp.expense_actual, B)}</div>
            <Bar actual={cmp.expense_actual} target={cmp.expense_cap} />
            <div className="text-xs text-muted-foreground mt-1">{cmp.expense_cap > 0 && cmp.expense_actual > cmp.expense_cap ? "⚠ تجاوزت السقف!" : `${pct(cmp.expense_actual, cmp.expense_cap)}% من السقف`}</div>
          </Card>
        </div>
      )}
      {cmp && (
        <div className="border border-border rounded-md bg-card">
          <div className="p-3 border-b font-semibold text-sm">سقوف المصروفات حسب الفئة (اختياري) — أدخل سقفاً لأي فئة ثم احفظ</div>
          <Table>
            <TableHeader><TableRow><TableHead>الفئة</TableHead><TableHead>السقف الشهري ({B})</TableHead><TableHead>المصروف الفعلي</TableHead><TableHead>الحالة</TableHead></TableRow></TableHeader>
            <TableBody>
              {EXP_CATEGORIES.map((c) => {
                const actual = cmp.expense_by_category?.[c] || 0;
                const capv = +catCaps[c] || 0;
                const over = capv > 0 && actual > capv;
                return (
                  <TableRow key={c}>
                    <TableCell className="font-semibold">{c}</TableCell>
                    <TableCell>
                      <Input type="number" value={catCaps[c] ?? ""} onChange={(e) => setCatCaps({ ...catCaps, [c]: e.target.value })} className="h-8 w-32" data-testid={`cat-cap-${c}`} placeholder="—" />
                    </TableCell>
                    <TableCell className={`tabular-nums ${over ? "text-red-600 font-bold" : ""}`}>{fmtMoney(actual, B)}</TableCell>
                    <TableCell className="text-xs">{capv <= 0 ? "—" : over ? <span className="text-red-600 font-bold">⚠ تجاوز</span> : <span className="text-emerald-600">ضمن السقف</span>}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          <div className="p-3 border-t"><Button size="sm" onClick={save} data-testid="budget-caps-save">حفظ السقوف</Button></div>
        </div>
      )}
    </div>
  );
}

function CostCenters({ cur }) {
  const [items, setItems] = useState([]);
  const [report, setReport] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [name, setName] = useState("");
  const [whId, setWhId] = useState("");
  const load = async () => {
    try {
      const [l, r, w] = await Promise.all([
        api.get("/cost-centers"),
        api.get("/reports/cost-centers"),
        api.get("/warehouses").catch(() => ({ data: [] })),
      ]);
      setItems(l.data); setReport(r.data); setWarehouses(w.data || []);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line
  const add = async () => {
    if (!name.trim()) return;
    try { await api.post("/cost-centers", { name: name.trim(), warehouse_id: whId || null }); setName(""); setWhId(""); toast.success("تمت الإضافة"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const setWarehouse = async (c, warehouse_id) => {
    try { await api.put(`/cost-centers/${c.id}`, { name: c.name, note: c.note || "", warehouse_id: warehouse_id || null }); toast.success("تم ربط المخزن"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const del = async (id) => { if (!confirm("حذف مركز التكلفة؟")) return; try { await api.delete(`/cost-centers/${id}`); load(); } catch (e) { toast.error(formatErr(e)); } };
  const whName = (id) => warehouses.find(w => w.id === id)?.name || "";
  const B = report?.base_currency || cur;
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="space-y-3">
        <div className="flex gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="اسم الفرع / مركز التكلفة" data-testid="cc-name" onKeyDown={(e) => e.key === "Enter" && add()} />
          <Select value={whId || "__none__"} onValueChange={(v) => setWhId(v === "__none__" ? "" : v)}>
            <SelectTrigger className="w-40" data-testid="cc-warehouse-new"><SelectValue placeholder="مخزن الفرع" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">بدون مخزن</SelectItem>
              {warehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={add} data-testid="cc-add"><Plus className="w-4 h-4 me-1" /> إضافة</Button>
        </div>
        <div className="text-xs text-muted-foreground">اربط كل فرع بمخزن مستقل ليُخصم/يُضاف مخزونه من مخزن الفرع تلقائياً في نقطة البيع والفواتير، ويظهر ربح كل فرع هنا.</div>
        <div className="border border-border rounded-md bg-card">
          <Table>
            <TableHeader><TableRow><TableHead>المركز / الفرع</TableHead><TableHead>المخزن المربوط</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {items.length === 0 && <TableRow><TableCell colSpan={3} className="text-center py-6 text-muted-foreground">لا توجد مراكز</TableCell></TableRow>}
              {items.map((c) => (
                <TableRow key={c.id} data-testid={`cc-row-${c.id}`}>
                  <TableCell className="font-semibold">{c.name}</TableCell>
                  <TableCell>
                    <Select value={c.warehouse_id || "__none__"} onValueChange={(v) => setWarehouse(c, v === "__none__" ? "" : v)}>
                      <SelectTrigger className="h-8 w-36 text-xs" data-testid={`cc-warehouse-${c.id}`}><SelectValue placeholder="بدون مخزن" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">بدون مخزن</SelectItem>
                        {warehouses.map(w => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="text-end"><Button size="icon" variant="ghost" className="w-7 h-7 text-destructive" onClick={() => del(c.id)}><Trash2 className="w-3.5 h-3.5" /></Button></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {warehouses.length === 0 && <div className="text-xs text-amber-600">أضِف مخازن من صفحة «المخازن» لتتمكن من فصل مخزون كل فرع.</div>}
      </div>
      <div className="border border-border rounded-md bg-card">
        <div className="p-3 border-b font-semibold text-sm flex items-center justify-between flex-wrap gap-2">
          <span>الربح حسب مركز التكلفة / الفرع</span>
          <span className="text-xs text-muted-foreground">صافي الربح {fmtMoney(report?.total_profit || 0, B)}</span>
        </div>
        <Table>
          <TableHeader><TableRow><TableHead>المركز</TableHead><TableHead>الإيراد</TableHead><TableHead>المصروفات</TableHead><TableHead>الربح</TableHead></TableRow></TableHeader>
          <TableBody>
            {(!report?.rows || report.rows.length === 0) && <TableRow><TableCell colSpan={4} className="text-center py-6 text-muted-foreground">لا توجد بيانات</TableCell></TableRow>}
            {report?.rows?.map((r, i) => (
              <TableRow key={i}>
                <TableCell className="font-semibold">{r.cost_center}</TableCell>
                <TableCell className="tabular-nums text-emerald-700">{fmtMoney(r.revenue, B)}</TableCell>
                <TableCell className="tabular-nums text-red-600">{fmtMoney(r.expense, B)}</TableCell>
                <TableCell className={`tabular-nums font-bold ${r.profit >= 0 ? "text-primary" : "text-destructive"}`}>{fmtMoney(r.profit, B)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function InvoiceSettings() {
  const [tpl, setTpl] = useState({ logo_url: "", logo_file_id: null, invoice_layout: "classic", primary_color: "#1E3A8A", header_text: "", footer_text: "", show_tax_id: false, tax_id: "", invoice_prefix: "", payment_terms: "", invoice_design: {} });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/company/template").then(({ data }) => { if (data && Object.keys(data).length) setTpl(t => ({ ...t, ...data })); }).catch(() => {}); }, []);
  const save = async () => {
    setBusy(true);
    try { await api.put("/company/template", tpl); toast.success("تم حفظ إعدادات وتصميم الفاتورة"); }
    catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  return <div className="max-w-7xl space-y-4">
    <InvoiceTemplateDesigner tpl={tpl} setTpl={setTpl} />
    <div className="flex justify-end"><Button onClick={save} disabled={busy} data-testid="tpl-save"><Save className="w-4 h-4 me-1" />{busy ? "جارٍ الحفظ..." : "حفظ إعدادات وتصميم الفاتورة"}</Button></div>
  </div>;
}

function Recurring({ cur, currencies = [] }) {
  const CURRENCIES = currencies.length ? currencies : [cur];
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ category: "إيجار", amount: "", currency: cur, day_of_month: 1, description: "" });
  const load = async () => { try { const { data } = await api.get("/recurring-expenses"); setItems(data); } catch (e) { toast.error(formatErr(e)); } };
  useEffect(() => { load(); }, []); // eslint-disable-line
  const add = async () => {
    if (+form.amount <= 0) return toast.error("أدخل مبلغاً");
    try { await api.post("/recurring-expenses", { ...form, amount: +form.amount, day_of_month: +form.day_of_month }); toast.success("تمت الإضافة"); setForm({ ...form, amount: "", description: "" }); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const del = async (id) => { if (!confirm("حذف المصروف المتكرر؟")) return; try { await api.delete(`/recurring-expenses/${id}`); load(); } catch (e) { toast.error(formatErr(e)); } };
  const runNow = async () => { try { const { data } = await api.post("/recurring-expenses/run"); toast.success(`تم ترحيل ${data.posted} مصروف لهذا الشهر`); load(); } catch (e) { toast.error(formatErr(e)); } };
  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <p className="text-sm text-muted-foreground">تُرحَّل هذه المصروفات تلقائياً كل شهر (مرة واحدة لكل شهر) وتُخصم من الصندوق. يمكنك الترحيل يدوياً الآن أيضاً.</p>
        <Button onClick={runNow} className="gap-1 bg-emerald-600 hover:bg-emerald-700" data-testid="recurring-run"><PlayCircle className="w-4 h-4" /> ترحيل مستحقات هذا الشهر</Button>
      </div>
      <Card className="p-4">
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2 items-end">
          <div><Label className="text-xs">الفئة</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger data-testid="recurring-category"><SelectValue /></SelectTrigger>
              <SelectContent>{EXP_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">المبلغ</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="recurring-amount" /></div>
          <div><Label className="text-xs">العملة</Label>
            <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">يوم الشهر</Label><Input type="number" min={1} max={28} value={form.day_of_month} onChange={(e) => setForm({ ...form, day_of_month: e.target.value })} data-testid="recurring-day" /></div>
          <div className="md:col-span-1"><Label className="text-xs">الوصف</Label><Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="اختياري" /></div>
          <Button onClick={add} data-testid="recurring-add"><Plus className="w-4 h-4 me-1" /> إضافة</Button>
        </div>
      </Card>
      <div className="border border-border rounded-md bg-card">
        <Table>
          <TableHeader><TableRow><TableHead>الفئة</TableHead><TableHead>المبلغ</TableHead><TableHead>اليوم</TableHead><TableHead>الوصف</TableHead><TableHead>آخر ترحيل</TableHead><TableHead></TableHead></TableRow></TableHeader>
          <TableBody>
            {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground">لا توجد مصروفات متكررة</TableCell></TableRow>}
            {items.map((r) => (
              <TableRow key={r.id} data-testid={`recurring-row-${r.id}`}>
                <TableCell className="font-semibold">{r.category}</TableCell>
                <TableCell className="tabular-nums font-bold">{fmtMoney(r.amount, r.currency)}</TableCell>
                <TableCell className="tabular-nums">{r.day_of_month}</TableCell>
                <TableCell className="text-xs">{r.description || "—"}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{r.last_run_month || "—"}</TableCell>
                <TableCell className="text-end"><Button size="icon" variant="ghost" className="w-7 h-7 text-destructive" onClick={() => del(r.id)}><Trash2 className="w-3.5 h-3.5" /></Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}


function ScaleSettings() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/company/scale-config").then(({ data }) => setCfg(data)).catch(() => {}); }, []);
  const save = async () => {
    setSaving(true);
    try { await api.put("/company/scale-config", cfg); toast.success("تم حفظ إعدادات الميزان"); }
    catch (e) { toast.error(formatErr(e)); }
    finally { setSaving(false); }
  };
  if (!cfg) return <div className="p-6 text-muted-foreground">جارٍ التحميل...</div>;
  const set = (k, v) => setCfg({ ...cfg, [k]: v });
  // Live example of a scale barcode based on the current settings
  const example = `${cfg.prefix}${"0".repeat(Math.max(0, cfg.item_digits - 2))}42${"0".repeat(Math.max(0, cfg.value_digits - 4))}1500`.slice(0, 13);
  return (
    <div className="max-w-2xl space-y-4" data-testid="scale-settings">
      <Card className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="font-heading font-bold text-lg flex items-center gap-2"><Scale className="w-5 h-5 text-primary" /> باركود الميزان الإلكتروني</div>
            <p className="text-xs text-muted-foreground mt-1">اضبط صيغة الباركود التي يطبعها ميزانك ليقرأها الكاشير تلقائياً ويحسب الوزن والسعر.</p>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => set("enabled", e.target.checked)} data-testid="scale-enabled" className="w-4 h-4" />
            مُفعّل
          </label>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">البادئة (بداية الباركود)</Label>
            <Input value={cfg.prefix} onChange={(e) => set("prefix", e.target.value.replace(/[^0-9]/g, "").slice(0, 3))} placeholder="2" data-testid="scale-prefix" />
            <p className="text-[10px] text-muted-foreground mt-0.5">عادةً 2 (أو 20، 21...)</p>
          </div>
          <div>
            <Label className="text-xs">نوع القيمة المُضمّنة</Label>
            <Select value={cfg.mode} onValueChange={(v) => set("mode", v)}>
              <SelectTrigger data-testid="scale-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="weight">وزن (يُحسب السعر تلقائياً)</SelectItem>
                <SelectItem value="price">سعر نهائي مباشر</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">عدد خانات رمز الصنف</Label>
            <Input type="number" min="2" max="8" value={cfg.item_digits} onChange={(e) => set("item_digits", +e.target.value)} data-testid="scale-item-digits" />
          </div>
          <div>
            <Label className="text-xs">عدد خانات القيمة (وزن/سعر)</Label>
            <Input type="number" min="3" max="7" value={cfg.value_digits} onChange={(e) => set("value_digits", +e.target.value)} data-testid="scale-value-digits" />
          </div>
          {cfg.mode === "weight" ? (
            <div>
              <Label className="text-xs">مقسوم الوزن (لتحويله لكيلو)</Label>
              <Input type="number" min="1" value={cfg.weight_divisor} onChange={(e) => set("weight_divisor", +e.target.value)} data-testid="scale-weight-divisor" />
              <p className="text-[10px] text-muted-foreground mt-0.5">1000 = القيمة بالغرام</p>
            </div>
          ) : (
            <div>
              <Label className="text-xs">مقسوم السعر</Label>
              <Input type="number" min="1" value={cfg.price_divisor} onChange={(e) => set("price_divisor", +e.target.value)} data-testid="scale-price-divisor" />
              <p className="text-[10px] text-muted-foreground mt-0.5">100 = آخر رقمين كسور العملة</p>
            </div>
          )}
        </div>

        <div className="rounded-lg bg-muted/50 p-3 text-xs leading-relaxed">
          <div className="font-bold mb-1">مثال توضيحي بناءً على إعداداتك:</div>
          باركود مثل <code className="font-mono bg-background px-1 rounded">{example}</code> يعني: رمز صنف <b>42</b>،
          {cfg.mode === "weight" ? ` ووزن ${(1500 / cfg.weight_divisor)} كغ — يُحسب السعر = الوزن × سعر كيلو الصنف.` : ` وسعر نهائي ${(1500 / cfg.price_divisor)}.`}
          <br />ملاحظة: يُطابَق رمز الصنف مع حقل «الباركود» للمنتج في النظام.
        </div>

        <Button onClick={save} disabled={saving} data-testid="scale-save-btn">{saving ? "جارٍ الحفظ..." : "حفظ إعدادات الميزان"}</Button>
      </Card>
    </div>
  );
}
